import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import './theme.css';

// Racine scellée : applique la charte sombre uniquement à Capex Sales.
export function CxRoot({ className = '', children, ...rest }) {
  return <div className={`cx-root p-4 sm:p-6 ${className}`} {...rest}>{children}</div>;
}

export function CxLogo({ subtitle }) {
  return (
    <div className="flex items-center gap-3">
      <span className="cx-logo"><b>CAPE<span style={{ color: 'var(--accent)' }}>X</span></b><span className="cx-logo-sales">Sales</span></span>
      {subtitle && <span className="text-xs cx-muted-3">· {subtitle}</span>}
    </div>
  );
}

export function CxLogoBox() {
  return (
    <div className="cx-logobox" data-testid="cx-logobox">
      <div className="cx-logobox__main">CAPE<span style={{ color: 'var(--accent)' }}>X</span></div>
      <div className="cx-logobox__sub">WASABI BUSINESS</div>
      <div className="cx-logobox__sales">SALES</div>
    </div>
  );
}

export function CxBack({ label = "Retour", onClick, testid = 'cx-back' }) {
  return <button onClick={onClick} data-testid={testid} className="cx-link mb-4"><ArrowLeft className="w-4 h-4" /> {label}</button>;
}

export function CxTile({ label, icon: Icon, iconColor, badge, wide, onClick, testid }) {
  return (
    <button className={`cx-tile ${wide ? 'cx-tile--wide' : ''}`} onClick={onClick} data-testid={testid}>
      <span className="cx-tile__icon">{Icon && <Icon className="w-8 h-8" strokeWidth={1.75} style={{ color: iconColor || 'var(--accent)' }} />}</span>
      <span className="cx-tile__label">{label}</span>
      {(badge || badge === 0) && badge !== null ? (badge ? <span className="cx-tile__badge">{badge}</span> : null) : null}
    </button>
  );
}

export function CxCounter({ value, label, trend, testid }) {
  return (
    <div className="cx-counter" data-testid={testid}>
      <div className="cx-counter__num">{value}</div>
      <div className="cx-counter__label">{label}</div>
      {trend != null && <div className={`cx-trend ${trend >= 0 ? 'up' : 'down'}`}>{trend >= 0 ? '▲' : '▼'} {trend >= 0 ? '+' : ''}{trend}%</div>}
    </div>
  );
}

export function CxChip({ children, selected, variant = 'accent', onClick, testid }) {
  const cls = selected ? (variant === 'green' ? 'cx-chip--on-green' : 'cx-chip--on') : '';
  return <button type="button" className={`cx-chip ${cls}`} onClick={onClick} data-testid={testid}>{children}</button>;
}

export function CxPastille({ value, tone = 'info', testid }) {
  return <span className={`cx-pastille cx-pastille--${tone}`} data-testid={testid}>{value}</span>;
}

export function CxTabs({ tabs, active, onChange, prefix = 'cx-tab' }) {
  const ref = useRef(null);
  const [canL, setCanL] = useState(false);
  const [canR, setCanR] = useState(false);
  const update = () => {
    const el = ref.current; if (!el) return;
    setCanL(el.scrollLeft > 2);
    setCanR(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };
  useEffect(() => {
    update();
    const el = ref.current; if (!el) return;
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const t = setTimeout(update, 60);
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);
  const scrollBy = (dir) => { const el = ref.current; if (!el) return; el.scrollBy({ left: dir * Math.max(140, el.clientWidth * 0.6), behavior: 'smooth' }); };
  return (
    <div className="cx-tabs-wrap mb-4">
      {canL && <span className="cx-tabs-fade cx-tabs-fade--l" aria-hidden="true" />}
      {canR && <span className="cx-tabs-fade cx-tabs-fade--r" aria-hidden="true" />}
      {canL && <button type="button" className="cx-tabs-arrow cx-tabs-arrow--l" onClick={() => scrollBy(-1)} aria-label="Onglets précédents" data-testid="cx-tabs-arrow-left"><ChevronLeft className="w-4 h-4" /></button>}
      {canR && <button type="button" className="cx-tabs-arrow cx-tabs-arrow--r" onClick={() => scrollBy(1)} aria-label="Onglets suivants" data-testid="cx-tabs-arrow-right"><ChevronRight className="w-4 h-4" /></button>}
      <div className="cx-tabs pb-px" ref={ref}>
        {tabs.map((t) => (
          <button key={t} className={`cx-tab ${active === t ? 'cx-tab--active' : ''}`} onClick={() => onChange(t)} data-testid={`${prefix}-${t}`}>{t}</button>
        ))}
      </div>
    </div>
  );
}

export function CxAccordion({ title, defaultOpen, children, testid, action }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="cx-acc">
      <div className="cx-acc__head">
        <button onClick={() => setOpen((o) => !o)} data-testid={testid}
          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
          <span className="cx-acc__title">{title}</span>
          <ChevronDown className={`w-4 h-4 cx-acc__chevron ${open ? 'open' : ''}`} />
        </button>
        {action && <span style={{ flexShrink: 0, marginLeft: 10, display: 'inline-flex' }}>{action}</span>}
      </div>
      {open && <div className="cx-acc__body">{children}</div>}
    </div>
  );
}

export function CxKV({ label, value, onEdit }) {
  return (
    <div className="cx-kv">
      <span className="cx-kv__label">{label}</span>
      <span className="cx-kv__value">{value}</span>
    </div>
  );
}

export function CxField({ label, children }) {
  return <div><label className="cx-label">{label}</label>{children}</div>;
}

export function CxButton({ children, variant = 'primary', onClick, disabled, type = 'button', testid, className = '' }) {
  return <button type={type} disabled={disabled} onClick={onClick} data-testid={testid} className={`cx-btn cx-btn--${variant} ${className}`}>{children}</button>;
}

export function CxSearch({ value, onChange, placeholder, testid, autoFocus }) {
  return (
    <div className="cx-search">
      <Search className="w-4 h-4 cx-search__icon" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} autoFocus={autoFocus} />
    </div>
  );
}

export function CxItem({ avatar, title, subtitle, right, onClick, testid }) {
  return (
    <button className="cx-item" onClick={onClick} data-testid={testid}>
      {avatar && <span className="cx-item__avatar">{avatar}</span>}
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate" style={{ color: 'var(--ink)' }}>{title}</div>
        {subtitle && <div className="text-xs truncate cx-muted">{subtitle}</div>}
      </div>
      {right}
    </button>
  );
}

export function CxEmpty({ children }) { return <div className="cx-empty cx-surface" style={{ padding: 40 }}>{children}</div>; }

export function CxBadge({ children, tone = 'client' }) { return <span className={`cx-badge cx-badge--${tone}`}>{children}</span>; }

export function CxModal({ title, message, children, actions, onClose, testid }) {
  return (
    <div className="cx-modal-overlay" onClick={onClose}>
      <div className="cx-modal" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-start justify-between gap-3">
          <div className="cx-modal__title">{title}</div>
          {onClose && <button onClick={onClose} className="cx-link"><X className="w-5 h-5" /></button>}
        </div>
        {message && <div className="cx-modal__msg">{message}</div>}
        {children}
        {actions && <div className="flex flex-wrap justify-end gap-2 mt-5">{actions}</div>}
      </div>
    </div>
  );
}
